import React, { useState } from 'react';
import * as Icons from 'lucide-react';
import { cn } from '../../lib/utils';
import { getModuleVideoId, getModuleImage, getModuleOutcomes, youtubeThumb } from '../../lib/moduleVideos';
import VideoLightbox from './VideoLightbox';
import ImageLightbox from './ImageLightbox';

const colorMap = {
  purple: 'bg-purple-50 text-purple',
  royal:  'bg-royal-50 text-royal',
  cyan:   'bg-cyan-50 text-cyan',
  mint:   'bg-mint-50 text-mint',
};

export function Eyebrow({ children, color = 'purple', className = '' }) {
  const map = { purple: 'text-purple', royal: 'text-royal', cyan: 'text-cyan', mint: 'text-mint' };
  return <div className={cn('text-[11.5px] font-bold tracking-[0.14em]', map[color], className)}>{children}</div>;
}

export function SectionHeading({ eyebrow, eyebrowColor = 'purple', title, desc, className = '', center = false }) {
  return (
    <div className={cn(center ? 'text-center' : '', className)}>
      {eyebrow && <Eyebrow color={eyebrowColor}>{eyebrow}</Eyebrow>}
      <h2 className="mt-2 text-[30px] md:text-[36px] font-extrabold leading-tight tracking-tight text-navy">{title}</h2>
      {desc && <p className={cn('mt-3 max-w-2xl text-[15px] leading-relaxed text-ink-500', center && 'mx-auto')}>{desc}</p>}
    </div>
  );
}

export function IconBadge({ icon = 'Sparkles', color = 'purple', size = 'md' }) {
  const Icon = Icons[icon] || Icons.Sparkles;
  const sizes = { sm: 'h-9 w-9 rounded-lg', md: 'h-11 w-11 rounded-xl', lg: 'h-14 w-14 rounded-2xl' };
  const iconSize = { sm: 'h-4 w-4', md: 'h-5 w-5', lg: 'h-6 w-6' };
  return (
    <span className={cn('inline-flex items-center justify-center', sizes[size], colorMap[color])}>
      <Icon className={cn(iconSize[size], 'stroke-[1.8]')} />
    </span>
  );
}

export function LetterBadge({ letter, color = 'purple', size = 'md' }) {
  const sizes = { sm: 'h-8 w-8 text-[11px] rounded-lg', md: 'h-10 w-10 text-[12px] rounded-xl' };
  return <span className={cn('inline-flex items-center justify-center font-bold', sizes[size], colorMap[color])}>{letter}</span>;
}

export function ContextStrip({ items = [] }) {
  return (
    <div className="rounded-2xl border border-canvas-200 bg-white p-2 shadow-soft">
      <div className="grid grid-cols-2 gap-1 md:grid-cols-5">
        {items.map((it, i) => (
          <div key={i} className={cn('px-4 py-3', i > 0 ? 'md:border-l md:border-canvas-200' : '')}>
            <div className="text-[10.5px] font-bold tracking-[0.12em] text-purple">{it.k}</div>
            <div className="mt-1 text-[13.5px] font-semibold text-navy">{it.v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function InfoCard({ icon, letter, color = 'purple', title, desc, className = '', bordered = true }) {
  return (
    <div className={cn('rounded-2xl bg-white p-5 shadow-soft', bordered && 'border border-canvas-200', 'transition-transform hover:-translate-y-0.5 hover:shadow-card', className)}>
      <div className="flex items-start gap-3">
        {icon ? <IconBadge icon={icon} color={color} /> : letter ? <LetterBadge letter={letter} color={color} /> : null}
        <div className="flex-1">
          <h3 className="text-[15px] font-bold text-navy leading-snug">{title}</h3>
          {desc && <p className="mt-1.5 text-[13px] leading-relaxed text-ink-500">{desc}</p>}
        </div>
      </div>
    </div>
  );
}

export function GradientButton({ to, href, onClick, children, variant = 'gradient', className = '', ...rest }) {
  const base = 'inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-[13.5px] font-semibold transition-all';
  const variants = {
    gradient: 'bg-brand-gradient text-white shadow-soft hover:brightness-110',
    outline:  'border border-canvas-200 bg-white text-navy hover:bg-canvas-100',
    dark:     'bg-navy text-white hover:bg-navy-800',
  };
  const cls = `${base} ${variants[variant]} ${className}`;
  if (to) { const Link = require('react-router-dom').Link; return <Link to={to} className={cls} {...rest}>{children}</Link>; }
  if (href) return <a href={href} className={cls} {...rest}>{children}</a>;
  return <button onClick={onClick} className={cls} {...rest}>{children}</button>;
}

export function CTABanner({ title, desc, primary, secondary, className = '' }) {
  return (
    <div className={cn('rounded-3xl bg-brand-gradient p-8 md:p-10 text-white shadow-card', className)}>
      <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="text-[24px] md:text-[28px] font-extrabold leading-tight">{title}</h3>
          {desc && <p className="mt-2 max-w-2xl text-[14px] text-white/85">{desc}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {secondary && (
            <GradientButton to={secondary.to} variant="outline" className="!bg-white/10 !text-white !border-white/30 hover:!bg-white/20">{secondary.label}</GradientButton>
          )}
          {primary && (
            <GradientButton to={primary.to} variant="outline" className="!bg-white !text-navy !border-white/0 hover:!bg-canvas-100">{primary.label}</GradientButton>
          )}
        </div>
      </div>
    </div>
  );
}

export function VideoCard({ title, chapter, gradient = 'from-purple to-royal', status, aspect = 'video' }) {
  const videoId = getModuleVideoId(title);
  const imageUrl = !videoId ? getModuleImage(title) : null;
  const outcomes = getModuleOutcomes(title);
  const [openVideo, setOpenVideo] = useState(false);
  const [openImage, setOpenImage] = useState(false);
  const clickable = Boolean(videoId || imageUrl);
  const openMedia = () => { if (videoId) setOpenVideo(true); else if (imageUrl) setOpenImage(true); };
  return (
    <>
      <div
        className={cn('group overflow-hidden rounded-2xl bg-white shadow-soft border border-canvas-200 transition-transform hover:-translate-y-0.5 hover:shadow-card', clickable && 'cursor-pointer')}
        {...(clickable
          ? {
              role: 'button',
              tabIndex: 0,
              onClick: openMedia,
              onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openMedia(); } },
              'data-testid': `video-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
            }
          : {})}
      >
        <div className={cn('relative flex items-center justify-center overflow-hidden', aspect === 'video' ? 'aspect-video' : 'aspect-[4/3]', !videoId && !imageUrl && 'bg-gradient-to-br', !videoId && !imageUrl && gradient)}>
          {videoId && (
            <>
              <img src={youtubeThumb(videoId, 'hqdefault')} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-black/25 group-hover:bg-black/15 transition-colors" />
              <button aria-label={`Play ${title}`} onClick={(e) => { e.stopPropagation(); setOpenVideo(true); }} className="relative grid h-12 w-12 place-items-center rounded-full bg-white/95 text-navy shadow-soft play-glow hover:scale-105 transition-transform">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
              </button>
            </>
          )}
          {imageUrl && (
            <>
              <img src={imageUrl} alt={title} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-colors" />
              <span className="absolute right-3 bottom-3 grid h-9 w-9 place-items-center rounded-full bg-white/95 text-navy shadow-soft opacity-0 group-hover:opacity-100 transition-opacity">
                <Icons.Maximize2 className="h-4 w-4" />
              </span>
            </>
          )}
          {status && (
            <span className={cn('absolute left-3 top-3 rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wide uppercase', status === 'Upcoming' ? 'bg-amber-100 text-amber-700' : 'bg-mint-50 text-mint-600')}>{status}</span>
          )}
        </div>
        <div className="p-4">
          {chapter && <div className="text-[10.5px] font-bold tracking-[0.14em] text-purple">{chapter.toUpperCase()}</div>}
          <div className="mt-1 text-[14px] font-bold text-navy">{title}</div>
        </div>
      </div>
      {openVideo && videoId && <VideoLightbox videoId={videoId} title={title} onClose={() => setOpenVideo(false)} />}
      {openImage && imageUrl && <ImageLightbox imageUrl={imageUrl} title={title} outcomes={outcomes} onClose={() => setOpenImage(false)} />}
    </>
  );
}

export function StepChip({ n, color = 'purple' }) {
  const map = { purple: 'bg-purple text-white', royal: 'bg-royal text-white', cyan: 'bg-cyan text-white', mint: 'bg-mint text-white' };
  return <span className={cn('inline-flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold', map[color])}>{n}</span>;
}
